import { env } from '../config/env';
import { createStubModule } from './stubModule';
import { createCameraKitModule } from './cameraKitModule';
import { createRemoteModule } from './remoteModule';
import { warmCameraKit } from './cameraKitWarm';
import type { ARModule } from './types';
import { devLog } from '../lib/devLog';

export type { ARModule, ARConfig } from './types';
export { AR_CANVAS_WIDTH, AR_CANVAS_HEIGHT } from './types';
export { warmCameraKit };
export {
  startArWarm,
  retryArWarmNow,
  arWarmStatus,
  useArWarmStatus,
  type ArWarmState,
  type ArWarmStatus,
} from './cameraKitWarm';

/**
 * The only place the app decides which AR implementation it is talking to.
 * Everything downstream sees an `ARModule` and cannot tell the difference.
 *
 * VITE_AR_MODULE=stub (default) | camera-kit | remote
 *
 * `camera-kit` is what ships. `stub` is for working locally without a Camera Kit
 * token — it is not a fallback, and nothing selects it automatically.
 */
/** A module the flow connected ahead of the capture screen, not yet claimed. */
let prepared: ARModule | null = null;

/**
 * Have a connected module waiting for the capture screen that follows.
 *
 * Called on the attract tap and again when the result screen mounts (a retake
 * may follow). The instruction clip covers the 1.2-2s the negigotiation and the
 * render machine's lens reset take, so the guest arrives at a camera that is
 * already showing them rather than at "Opening the camera…". An unclaimed
 * module from earlier — a guest who walked away, a result screen that went
 * to the phone step instead of a retake — is kept and re-armed rather than
 * rebuilt: its `preconnect()` reuses a healthy link and asks for a fresh lens,
 * or reconnects a dead one. Nothing happens for the other modules — they have
 * no connection to make.
 */
export function prepareARModule(): void {
  if (env.arModule !== 'remote') return;
  if (!prepared) prepared = createRemoteModule();
  prepared.preconnect?.();
}

export function createARModule(): ARModule {
  if (prepared) {
    const module = prepared;
    prepared = null;
    devLog('[ar] using the pre-connected remote module');
    return module;
  }
  if (env.arModule === 'remote') {
    devLog('[ar] using the remote render machine');
    return createRemoteModule();
  }
  if (env.arModule === 'camera-kit') {
    devLog('[ar] using Snap Camera Kit Web SDK');
    return createCameraKitModule();
  }
  devLog('[ar] using stub module');
  return createStubModule();
}

export const usingStub = env.arModule === 'stub';
